// =============================================================================
// features/sales/campaigns/detail/CampaignDetailContent.tsx — Busca a
// campanha por id e orquestra cabeçalho + abas. Estados de carregando/404/
// erro tratados aqui (DoD: "campanha fora do escopo... resulta em 404, não
// em tela vazia").
// =============================================================================

import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useCampaignDetail,
  usePauseCampaign,
  useResumeCampaign,
} from '../../../../hooks/sales/useCampaignDetail';

import { CampaignDetailHeader } from './CampaignDetailHeader';
import {
  CampaignDetailErrorState,
  CampaignDetailLoadingState,
  CampaignDetailNotFoundState,
} from './CampaignDetailStates';
import { CampaignDetailTabs } from './CampaignDetailTabs';
import { ActivityTab } from './tabs/ActivityTab';
import { OverviewTab } from './tabs/OverviewTab';
import { PortfolioTab } from './tabs/PortfolioTab';
import { ReturnsTab } from './tabs/ReturnsTab';
import { SettingsTab } from './tabs/SettingsTab';
import { useDetailTab } from './useDetailTab';

const MANAGE_PERMISSION = 'sales_campaigns:manage';

export function CampaignDetailContent({
  campaignId,
  canManage,
}: {
  campaignId: string;
  canManage: boolean;
}): React.JSX.Element {
  const navigate = useNavigate();
  const { campaign, isLoading, isError, isNotFound, refetch } = useCampaignDetail(campaignId);
  const { tab, setTab } = useDetailTab();
  const pause = usePauseCampaign(campaignId);
  const resume = useResumeCampaign(campaignId);

  if (isLoading) return <CampaignDetailLoadingState />;
  if (isNotFound) {
    return <CampaignDetailNotFoundState onBack={() => navigate('/campanhas')} />;
  }
  if (isError || !campaign) {
    return <CampaignDetailErrorState onRetry={refetch} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <CampaignDetailHeader
        campaign={campaign}
        canManage={canManage}
        onPause={pause.mutateAsync}
        onResume={resume.mutateAsync}
        isTransitionPending={pause.isPending || resume.isPending}
      />

      <CampaignDetailTabs active={tab} onChange={setTab} />

      <div role="tabpanel">
        {tab === 'visao-geral' && <OverviewTab campaign={campaign} />}
        {tab === 'carteira' && <PortfolioTab campaign={campaign} canManage={canManage} />}
        {tab === 'retornos' && <ReturnsTab campaign={campaign} />}
        {tab === 'atividade' && <ActivityTab />}
        {tab === 'configuracao' && <SettingsTab campaign={campaign} canManage={canManage} />}
      </div>
    </div>
  );
}

export { MANAGE_PERMISSION };
